import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import type { ReactNode } from 'react';

import type { ReactTable, RowData } from '@tanstack/react-table';

import { cx } from '@/shared/lib/cx';

import styles from './DataTable.module.scss';
import type { DataTableFeatures } from './tableFeatures';

export interface DataTableProps<TData extends RowData, TSelected> {
  table: ReactTable<DataTableFeatures, TData, TSelected>;
  /**
   * Что это за таблица, словами. Читается скринридером перед содержимым и
   * визуально скрыто: заголовок раздела рядом объясняет то же самое глазами.
   */
  caption: string;
  /** Первая загрузка: строк ещё не было ни одной. */
  loading?: boolean;
  /**
   * Обновление на месте: строки уже есть, но данные перезапрашиваются.
   *
   * Отдельно от `loading` намеренно. Подменять готовую таблицу скелетом при
   * каждом обновлении — значит гасить и перерисовывать весь экран там, где
   * поменяется одна ячейка.
   */
  refreshing?: boolean;
  /** Чем занять место, когда строк нет. */
  empty?: ReactNode;
  /** Сколько строк рисовать в скелете. */
  skeletonRows?: number;
  className?: string;
}

const ARIA_SORT = {
  asc: 'ascending',
  desc: 'descending',
} as const;

/**
 * Таблица данных.
 *
 * Разметку, оформление и доступность делает этот компонент, а состояние —
 * сортировку, выбор, страницы — держит вызывающий код: рано или поздно оно
 * переедет на сервер и в адресную строку, и компонент об этом знать не
 * должен.
 */
export function DataTable<TData extends RowData, TSelected>({
  table,
  caption,
  loading = false,
  refreshing = false,
  empty,
  skeletonRows = 5,
  className,
}: DataTableProps<TData, TSelected>) {
  const rows = table.getRowModel().rows;
  const columns = table.getVisibleLeafColumns();

  return (
    <div className={cx(styles['scroller'], className)}>
      <table className={styles['table']} aria-busy={loading || refreshing}>
        <caption className={styles['table__caption']}>{caption}</caption>

        <thead className={styles['table__head']}>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => {
                const meta = header.column.columnDef.meta;
                const sorted = header.column.getIsSorted();
                const canSort = header.column.getCanSort();

                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={styles['table__th']}
                    // Порядок сортировки объявляется атрибутом, а не значком:
                    // значок скринридер не читает вовсе.
                    aria-sort={
                      canSort ? (sorted === false ? 'none' : ARIA_SORT[sorted]) : undefined
                    }
                    data-align={meta?.align}
                    style={meta?.width === undefined ? undefined : { width: meta.width }}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        className={styles['table__sort']}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className={styles['table__sort-label']}>
                          <table.FlexRender header={header} />
                        </span>

                        <span className={styles['table__sort-icon']} aria-hidden="true">
                          {sorted === 'asc' && <ArrowUp size={13} />}
                          {sorted === 'desc' && <ArrowDown size={13} />}
                          {sorted === false && <ChevronsUpDown size={13} />}
                        </span>
                      </button>
                    ) : (
                      <table.FlexRender header={header} />
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody className={cx(styles['table__body'], refreshing && styles['table__body--dim'])}>
          {loading &&
            Array.from({ length: skeletonRows }, (_, rowIndex) => (
              <tr key={`skeleton-${String(rowIndex)}`} className={styles['table__tr']}>
                {columns.map((column) => (
                  <td key={column.id} className={styles['table__td']}>
                    <span className={styles['table__skeleton']} />
                  </td>
                ))}
              </tr>
            ))}

          {!loading && rows.length === 0 && empty !== undefined && (
            <tr>
              <td className={styles['table__empty']} colSpan={columns.length}>
                {empty}
              </td>
            </tr>
          )}

          {!loading &&
            rows.map((row) => (
              <tr
                key={row.id}
                className={styles['table__tr']}
                data-selected={row.getIsSelected() ? '' : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={styles['table__td']}
                    data-align={cell.column.columnDef.meta?.align}
                  >
                    <table.FlexRender cell={cell} />
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
